import moment from "moment";

import { getEnvironment } from "utils";

import { CROP_TYPE_FCPO, CropFCPO } from "models";

import { SUMMER_CROPS } from "pages/admin/verifications/utils/summerCrops";

const JUNE = 5;
const JULY = 6;
const maxDaysAfterSummerCrop = 45;
const maxDaysAfterCrop = 90;
const cutoffDate = "11/01"; // After this harvest date we do not care about the rules anymore

type Warning = { title: string; description: string };
type WarningsByCrop = { [id: string]: Warning[] };

const summerCropIds: string[] = Object.values(SUMMER_CROPS);

export const getWarnings = (crops: CropFCPO[]): WarningsByCrop => {
  const cropsSortedByHarvestDesc = crops.toSorted(
    (a, b) => moment(b.harvestEndDate ?? b.fallbackDates.harvestEndDate).valueOf() - moment(a.harvestEndDate ?? a.fallbackDates.harvestEndDate).valueOf()
  );

  return crops.reduce<WarningsByCrop>((warningsByCrop, crop) => {
    const harvestDate = moment(crop.harvestEndDate ?? crop.fallbackDates.harvestEndDate);
    const seedingDate = moment(crop.seedingStartDate ?? crop.fallbackDates.seedingStartDate);
    const harvestMonth = harvestDate.month();

    if (crop.cropType === CROP_TYPE_FCPO.UNDERSOWING || harvestDate.isAfter(`${harvestDate.year()}/${cutoffDate}`)) {
      return warningsByCrop;
    }

    let seedingDiff: number = Infinity;

    // For this crop (if harvested in june/july) check if the next seeded crop is at the most x days later
    // or if not harvested in june july at the most y days later
    crops.forEach((secondCrop) => {
      if (secondCrop.cropType !== CROP_TYPE_FCPO.UNDERSOWING) {
        const seedingDate = moment(secondCrop.seedingStartDate ?? secondCrop.fallbackDates.seedingStartDate);
        const diff = seedingDate.diff(harvestDate, "days");

        if (diff > 0 && diff < seedingDiff) {
          seedingDiff = diff;
        }
      }
    });

    // Find the previous crop
    const previousCrop = cropsSortedByHarvestDesc.find(
      (secondCrop) =>
        secondCrop.cropType !== CROP_TYPE_FCPO.UNDERSOWING && moment(secondCrop.harvestEndDate ?? secondCrop.fallbackDates.harvestEndDate).isBefore(seedingDate)
    );

    const warnings: Warning[] = [];

    // Only warning 3 should be active on prod for now
    if (!getEnvironment().admin) {
      // Warning 1: Summer cover crop missing after June/July main crop
      if (crop.cropType === CROP_TYPE_FCPO.MAIN && (harvestMonth === JUNE || harvestMonth === JULY) && seedingDiff > maxDaysAfterSummerCrop) {
        warnings.push({
          title: "Potential summer cover crop missing",
          description: `There is no crop seeded within ${maxDaysAfterSummerCrop} days after`,
        });
      }

      // Warning 2: Large gap between crops
      if (seedingDiff > maxDaysAfterCrop) {
        warnings.push({
          title: "Large period without a crop",
          description: `There is no crop seeded within ${maxDaysAfterCrop} days after`,
        });
      }
    }

    // Warning 3: Summer crop not preceded by cover crop
    if (summerCropIds.includes(crop.cropId) && previousCrop?.cropType !== CROP_TYPE_FCPO.COVER) {
      warnings.push({
        title: "Expected cover crop before summer crop",
        description: "Check if the farmer forgot to add a cover crop before this summer crop",
      });
    }

    if (warnings.length) {
      warningsByCrop[crop.id] = [...(warningsByCrop[crop.id] ?? []), ...warnings];
    }

    return warningsByCrop;
  }, {});
};
